import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import MobileNavigation from '../components/MobileNavigation';
import MobileAircraftDetails from '../components/MobileAircraftDetails';
import MobileTraining from '../components/MobileTraining';
import MobileReservationForm from '../components/MobileReservationForm';
import { API_BASE_URL } from '../config/api';
import { getLocationDisplayName } from '../utils/locationUtils';
import { canCancelReservationAtShortNotice, RESERVATION_CHANGE_REASONS, requiresReservationReason } from '../utils/reservationChangePolicy';

const MobileCalendar = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showAircraftDetails, setShowAircraftDetails] = useState(null);
  const [showTraining, setShowTraining] = useState(false);
  const [showReservationForm, setShowReservationForm] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [showCancellation, setShowCancellation] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [customCancellationReason, setCustomCancellationReason] = useState('');
  const [cancellationError, setCancellationError] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const hasMountedScheduleDate = React.useRef(false);
  const [isConfirmingSafetyPilot, setIsConfirmingSafetyPilot] = useState(false);
  const [isDenyingSafetyPilot, setIsDenyingSafetyPilot] = useState(false);
  const [confirmSafetyPilotMessage, setConfirmSafetyPilotMessage] = useState('');
  const [confirmSafetyPilotError, setConfirmSafetyPilotError] = useState(false);
  const { currentUser, aircraft, people, reservations, fetchReservations, locations = [], selectedLocationId, setSelectedLocationId } = useApp();

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (currentUser?.sessionId) {
      const dateStr = formatDate(selectedDate);
      fetchReservations(dateStr, dateStr);
    }
  }, [selectedDate, currentUser?.sessionId, fetchReservations]);

  useEffect(() => {
    if (!hasMountedScheduleDate.current) {
      hasMountedScheduleDate.current = true;
      return;
    }
    window.dispatchEvent(new Event('popup-question:schedule-day-viewed'));
  }, [selectedDate]);

  const dayReservations = reservations.filter(r => {
    if (!r.startDate) return false;
    const reservationDateStr = String(r.startDate).slice(0, 10);
    const selectedDateStr = formatDate(selectedDate);
    const locationId = r.locationId ?? locations.find(location => location.isDefault)?.id;
    return reservationDateStr === selectedDateStr && r.status === 'active' && (!selectedLocationId || String(locationId) === String(selectedLocationId));
  });

  

  const getAircraftName = (aircraftId, reservationAircraft) => {
    const plane = aircraft.find(a => a.id === aircraftId);
    if (plane) {
      return `${plane.model} - ${plane.tailNumber || plane.registration}`;
    }
    // Fallback to reservation's embedded aircraft data
    if (reservationAircraft) {
      return `${reservationAircraft.model} - ${reservationAircraft.tailNumber || reservationAircraft.registration}`;
    }
    return 'Unknown';
  };

  const getUserName = (userId) => {
    const user = people.find(p => p.id === userId);
    return user ? `${user.firstName || user.first_name} ${user.lastName || user.last_name}` : 'Unknown';
  };

  const getClientName = (reservation) => {
    const client = people.find((person) => Number(person.id) === Number(reservation?.userIdClient)) || reservation?.client;
    if (!client) return 'Unknown';
    return `${client.firstName || client.first_name || ''} ${client.lastName || client.last_name || ''}`.trim() || reservation?.clientName || reservation?.client_name || 'Unknown';
  };

  const convertToAmPm = (militaryTime) => {
    const hours = Math.floor(militaryTime / 100);
    const minutes = militaryTime % 100;
    const h12 = hours == 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const ampm = hours < 12 ? 'AM' : 'PM';
    return `${h12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const canClientAccessReservation = (reservation) => {
    if (currentUser?.user?.orgRole !== 'client') {
      return true;
    }

    return Number(reservation?.userIdClient) === Number(currentUser?.user?.id) || Number(reservation?.safetyPilotId) === Number(currentUser?.user?.id);
  };

  const canViewClientNames = currentUser?.user?.orgRole === 'staff'
    || currentUser?.user?.orgRole === 'admin'
    || currentUser?.user?.orgRole === 'superuser';

  const getSafetyPilotStatus = (reservation) => {
    const status = reservation?.safetyPilotStatus;
    if (status === 'confirmed' || status === 'denied' || status === 'unconfirmed') {
      return status;
    }
    return reservation?.safetyPilotId ? 'unconfirmed' : 'not-assigned';
  };

  const isCurrentUserAssignedSafetyPilot = (reservation) => {
    if (!reservation?.safetyPilotId) return false;
    return Number(currentUser?.user?.id) === Number(reservation.safetyPilotId);
  };

  const getSafetyPilotName = (reservation) => {
    if (!reservation?.safetyPilotId) return '';
    const user = people.find((person) => person.id === reservation.safetyPilotId) || reservation.safetyPilot;
    if (!user) return '';
    return `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() || user.username || '';
  };

  const canCurrentUserConfirmSafetyPilot = (reservation) => {
    if (!isCurrentUserAssignedSafetyPilot(reservation)) return false;
    const status = getSafetyPilotStatus(reservation);
    return status === 'unconfirmed' || status === 'denied';
  };

  const canCurrentUserDenySafetyPilot = (reservation) => {
    if (!isCurrentUserAssignedSafetyPilot(reservation)) return false;
    const status = getSafetyPilotStatus(reservation);
    return status === 'unconfirmed' || status === 'confirmed';
  };

  const handleConfirmSafetyPilot = async () => {
    if (!selectedReservation?.id || !canCurrentUserConfirmSafetyPilot(selectedReservation) || isConfirmingSafetyPilot) {
      return;
    }

    setIsConfirmingSafetyPilot(true);
    setConfirmSafetyPilotMessage('');
    setConfirmSafetyPilotError(false);
    try {
      const response = await fetch(`${API_BASE_URL}/reservation/safety-pilot/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({ reservationId: selectedReservation.id })
      });

      if (response.ok) {
        setConfirmSafetyPilotMessage('Safety pilot confirmed successfully');
        setConfirmSafetyPilotError(false);
        setSelectedReservation((previous) => previous ? { ...previous, safetyPilotStatus: 'confirmed' } : previous);
        const dateStr = formatDate(selectedDate);
        fetchReservations(dateStr, dateStr);
      } else {
        const errorText = await response.text();
        setConfirmSafetyPilotMessage(errorText || 'Failed to confirm safety pilot');
        setConfirmSafetyPilotError(true);
      }
    } catch (error) {
      setConfirmSafetyPilotMessage(error.message || 'Failed to confirm safety pilot');
      setConfirmSafetyPilotError(true);
    } finally {
      setIsConfirmingSafetyPilot(false);
    }
  };

  const handleDenySafetyPilot = async () => {
    if (!selectedReservation?.id || !canCurrentUserDenySafetyPilot(selectedReservation) || isDenyingSafetyPilot) {
      return;
    }

    setIsDenyingSafetyPilot(true);
    setConfirmSafetyPilotMessage('');
    setConfirmSafetyPilotError(false);
    try {
      const response = await fetch(`${API_BASE_URL}/reservation/safety-pilot/deny`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({ reservationId: selectedReservation.id })
      });

      if (response.ok) {
        const dateStr = formatDate(selectedDate);
        fetchReservations(dateStr, dateStr);
        setSelectedReservation(null);
      } else {
        const errorText = await response.text();
        setConfirmSafetyPilotMessage(errorText || 'Failed to deny safety pilot request');
        setConfirmSafetyPilotError(true);
      }
    } catch (error) {
      setConfirmSafetyPilotMessage(error.message || 'Failed to deny safety pilot request');
      setConfirmSafetyPilotError(true);
    } finally {
      setIsDenyingSafetyPilot(false);
    }
  };

  const cancelSelectedReservation = async () => {
    if (isCancelling || !selectedReservation || !canClientAccessReservation(selectedReservation) || isCurrentUserAssignedSafetyPilot(selectedReservation)) return;
    if (!canCancelReservationAtShortNotice(selectedReservation, currentUser?.user)) {
      setCancellationError('Only an instructor, admin, or superuser can cancel a flight lesson within 24 hours.');
      return;
    }
    const reason = (cancellationReason === 'other' ? customCancellationReason : cancellationReason).trim();
    if (requiresReservationReason(selectedReservation) && !reason) {
      setCancellationError('A reason is required to cancel a reservation within 28 hours.');
      return;
    }
    setIsCancelling(true);
    setCancellationError('');
    try {
      const response = await fetch(`${API_BASE_URL}/reservation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'session-id': currentUser?.sessionId },
        body: JSON.stringify({ id: selectedReservation.id, status: 'cancelled', ...(reason ? { cancellationReason: reason } : {}) })
      });
      if (!response.ok) {
        const text = await response.text();
        let error = text || 'Unable to cancel reservation.';
        try { error = JSON.parse(text).error || error; } catch {}
        throw new Error(error);
      }
      const dateStr = formatDate(selectedDate);
      await fetchReservations(dateStr, dateStr);
      setSelectedReservation(null);
      setShowCancellation(false);
    } catch (error) {
      setCancellationError(error.message || 'Unable to cancel reservation.');
    } finally { setIsCancelling(false); }
  };

  if (!currentUser?.sessionId) {
    window.location.href = '/login';
    return null;
  }

  return (
    <div className="mobile-screen" style={{ paddingTop: '72px', minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <MobileNavigation />
      
      {/* Date Selector */}
      <div className="mobile-section-header" style={{
        padding: '1rem', 
        backgroundColor: 'white', 
        borderBottom: '1px solid #dee2e6',
        position: 'sticky',
        top: '72px',
        zIndex: 100
      }}>
        {locations.length > 1 && <label style={{ display: 'block', marginBottom: '0.75rem' }}>Location
          <select aria-label="Schedule location" value={selectedLocationId} onChange={event => setSelectedLocationId(event.target.value)} style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}>
            {locations.map(location => <option key={location.id} value={location.id}>{getLocationDisplayName(location)}{location.airportCode ? ` (${location.airportCode})` : ''}</option>)}
          </select>
        </label>}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            aria-label="Previous day"
            onClick={() => {
              const newDate = new Date(selectedDate);
              newDate.setDate(newDate.getDate() - 1);
              setSelectedDate(newDate);
            }}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            ‹
          </button>
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
              {selectedDate.toLocaleDateString('en-US', { 
                weekday: 'long',
                month: 'long', 
                day: 'numeric',
                year: 'numeric'
              })}
            </div>
          </div>
          
          <button
            aria-label="Next day"
            onClick={() => {
              const newDate = new Date(selectedDate);
              newDate.setDate(newDate.getDate() + 1);
              setSelectedDate(newDate);
            }}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            ›
          </button>
        </div>
        
        <button
          onClick={() => {
            setShowReservationForm(true);
          }}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            marginTop: '1rem',
            cursor: 'pointer'
          }}
        >
          + New Reservation
        </button>
      </div>

      {/* Reservations List */}
      <div style={{ padding: '1rem' }}>
        
        {dayReservations.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem 1rem',
            color: '#666',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '1rem' }}>📅</div>
            <div style={{ fontSize: '18px', marginBottom: '0.5rem' }}>No reservations</div>
            <div style={{ fontSize: '14px' }}>Tap "New Reservation" to add one</div>
          </div>
        ) : (
          dayReservations.map((reservation) => (
            <div
              key={reservation.id}
              onClick={() => {
                // Client users can only click their own reservations
                if (!canClientAccessReservation(reservation)) {
                  return;
                }
                setConfirmSafetyPilotMessage('');
                setShowCancellation(false);
                setCancellationError('');
                setSelectedReservation(reservation);
              }}
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '1px solid #dee2e6',
                cursor: canClientAccessReservation(reservation) ? 'pointer' : 'default',
                opacity: canClientAccessReservation(reservation) ? 1 : 0.6
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
                  {getAircraftName(reservation.aircraftId, reservation.aircraft)}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  padding: '4px 8px', 
                  backgroundColor: reservation.type === 'Flight Training' ? '#e3f2fd' : '#fff3e0',
                  color: reservation.type === 'Flight Training' ? '#1976d2' : '#f57c00',
                  borderRadius: '12px',
                  fontWeight: 'bold'
                }}>
                  {reservation.type || 'Flight'}
                </div>
              </div>
              
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '0.5rem' }}>
                👤 {canViewClientNames ? getClientName(reservation) : 'Private'}
              </div>

              {reservation.safetyPilotId && (
                <div style={{ fontSize: '14px', color: reservation.safetyPilotStatus === 'unconfirmed' ? '#9a9a9a' : '#666', marginBottom: '0.5rem' }}>
                  ✈️ Safety Pilot: {getSafetyPilotName(reservation) || 'Assigned'}
                </div>
              )}
              
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '0.5rem' }}>
                🕐 {convertToAmPm(reservation.startTime)} - {convertToAmPm(reservation.endTime)}
              </div>
              
              {reservation.userIdInstructor && (
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '0.5rem' }}>
                  👨🏫 {getUserName(reservation.userIdInstructor)}
                </div>
              )}
              
              {reservation.comment && (
                <div style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  backgroundColor: '#f8f9fa',
                  padding: '8px',
                  borderRadius: '4px',
                  marginTop: '0.5rem'
                }}>
                  💬 {reservation.comment}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Reservation Details Modal */}
      {selectedReservation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>Reservation Details</h3>
              <button
                aria-label="Close reservation details"
                disabled={isCancelling}
                onClick={() => {
                  setConfirmSafetyPilotMessage('');
                  setShowCancellation(false);
                  setSelectedReservation(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <strong>Aircraft:</strong>
              <div style={{ marginTop: '5px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                {getAircraftName(selectedReservation.aircraftId, selectedReservation.aircraft)}
              </div>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <strong>Student:</strong>
              <div style={{ marginTop: '5px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                {canViewClientNames ? getClientName(selectedReservation) : 'Private'}
              </div>
            </div>

            {selectedReservation.safetyPilotId && (
              <div style={{ marginBottom: '15px' }}>
                <strong>Safety Pilot:</strong>
                <div style={{ marginTop: '5px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px', color: selectedReservation.safetyPilotStatus === 'unconfirmed' ? '#9a9a9a' : '#212529' }}>
                  {getSafetyPilotName(selectedReservation) || 'Assigned'}
                </div>
              </div>
            )}
            
            {selectedReservation.userIdInstructor && (
              <div style={{ marginBottom: '15px' }}>
                <strong>Instructor:</strong>
                <div style={{ marginTop: '5px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                  {getUserName(selectedReservation.userIdInstructor)}
                </div>
              </div>
            )}
            
            <div style={{ marginBottom: '15px' }}>
              <strong>Time:</strong>
              <div style={{ marginTop: '5px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                {convertToAmPm(selectedReservation.startTime)} - {convertToAmPm(selectedReservation.endTime)}
              </div>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <strong>Type:</strong>
              <div style={{ marginTop: '5px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                {selectedReservation.type || 'Flight'}
              </div>
            </div>
            
            {selectedReservation.comment && (
              <div style={{ marginBottom: '15px' }}>
                <strong>Notes:</strong>
                <div style={{ marginTop: '5px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                  {selectedReservation.comment}
                </div>
              </div>
            )}

            {confirmSafetyPilotMessage && (
              <div style={{ marginBottom: '15px', padding: '10px', borderRadius: '6px', backgroundColor: confirmSafetyPilotError ? '#f8d7da' : '#d4edda', color: confirmSafetyPilotError ? '#721c24' : '#155724', border: `1px solid ${confirmSafetyPilotError ? '#f5c6cb' : '#c3e6cb'}` }}>
                {confirmSafetyPilotMessage}
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              {canCurrentUserConfirmSafetyPilot(selectedReservation) && (
                <button
                  onClick={handleConfirmSafetyPilot}
                  disabled={isConfirmingSafetyPilot || isDenyingSafetyPilot || isCancelling}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#2a7a4b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: (isConfirmingSafetyPilot || isDenyingSafetyPilot) ? 'not-allowed' : 'pointer',
                    opacity: (isConfirmingSafetyPilot || isDenyingSafetyPilot) ? 0.6 : 1
                  }}
                >
                  {isConfirmingSafetyPilot ? 'Confirming...' : 'Confirm Safety Pilot'}
                </button>
              )}
              {canCurrentUserDenySafetyPilot(selectedReservation) && (
                <button
                  onClick={handleDenySafetyPilot}
                  disabled={isConfirmingSafetyPilot || isDenyingSafetyPilot || isCancelling}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#b02a37',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: (isConfirmingSafetyPilot || isDenyingSafetyPilot) ? 'not-allowed' : 'pointer',
                    opacity: (isConfirmingSafetyPilot || isDenyingSafetyPilot) ? 0.6 : 1
                  }}
                >
                  {isDenyingSafetyPilot ? 'Denying...' : 'Deny Safety Pilot'}
                </button>
              )}
              {!isCurrentUserAssignedSafetyPilot(selectedReservation) && (
                <button
                  disabled={isCancelling}
                  onClick={() => {
                    setSelectedReservation(null);
                    setConfirmSafetyPilotMessage('');
                    setShowAircraftDetails(selectedReservation);
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Aircraft
                </button>
              )}
              {!isCurrentUserAssignedSafetyPilot(selectedReservation) && (
                <button
                  disabled={isCancelling}
                  onClick={() => {
                    setSelectedReservation(null);
                    setConfirmSafetyPilotMessage('');
                    setShowTraining(selectedReservation);
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Lesson
                </button>
              )}
            </div>
            {!isCurrentUserAssignedSafetyPilot(selectedReservation) && canClientAccessReservation(selectedReservation) && <div style={{ position: 'sticky', bottom: '-20px', backgroundColor: 'white', padding: '1rem 0', marginTop: '1rem', borderTop: '1px solid #dee2e6' }}>
              {!canCancelReservationAtShortNotice(selectedReservation, currentUser?.user) && <p role="status" style={{ margin: '0 0 0.75rem' }}>Only an instructor, admin, or superuser can cancel a flight lesson within 24 hours.</p>}
              {cancellationError && <p role="alert" style={{ color: '#b02a37' }}>{cancellationError}</p>}
              {showCancellation ? <>
                <h4 style={{ margin: '0 0 0.75rem' }}>Cancel reservation?</h4>
                {requiresReservationReason(selectedReservation) && <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'grid', gap: '0.5rem' }}>Cancellation reason
                    <select aria-label="Cancellation reason" value={cancellationReason} disabled={isCancelling} onChange={event => setCancellationReason(event.target.value)} style={{ width: '100%', padding: '0.5rem' }}>
                      <option value="">Select a reason</option>
                      {RESERVATION_CHANGE_REASONS.map(reason => <option key={reason} value={reason}>{reason === 'other' ? 'Other' : reason}</option>)}
                    </select>
                  </label>
                  {cancellationReason === 'other' && <input aria-label="Other cancellation reason" value={customCancellationReason} disabled={isCancelling} onChange={event => setCustomCancellationReason(event.target.value)} placeholder="Please specify..." style={{ width: '100%', boxSizing: 'border-box', padding: '0.5rem', marginTop: '0.5rem' }} />}
                </div>}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button type="button" disabled={isCancelling} onClick={() => setShowCancellation(false)} style={{ flex: 1, padding: '12px' }}>Back</button>
                  <button type="button" disabled={isCancelling || !canCancelReservationAtShortNotice(selectedReservation, currentUser?.user) || (requiresReservationReason(selectedReservation) && (!cancellationReason || (cancellationReason === 'other' && !customCancellationReason.trim())))} onClick={cancelSelectedReservation} style={{ flex: 1, padding: '12px', backgroundColor: '#b02a37', color: 'white', border: 'none', borderRadius: '6px' }}>{isCancelling ? 'Cancelling...' : 'Confirm Cancel'}</button>
                </div>
              </> : <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button type="button" onClick={() => { setEditingReservation(selectedReservation); setSelectedReservation(null); setShowReservationForm(true); }} disabled={isCancelling} style={{ flex: 1, padding: '12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600 }}>Update</button>
                <button type="button" onClick={() => { setCancellationReason(''); setCustomCancellationReason(''); setCancellationError(''); setShowCancellation(true); }} disabled={isCancelling || !canCancelReservationAtShortNotice(selectedReservation, currentUser?.user)} style={{ flex: 1, padding: '12px', backgroundColor: '#b02a37', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600 }}>Cancel</button>
              </div>}
            </div>}
          </div>
        </div>
      )}

      {/* Aircraft Details Modal */}
      {showAircraftDetails && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          zIndex: 1000
        }}>
          <MobileAircraftDetails 
            reservation={showAircraftDetails} 
            onClose={() => setShowAircraftDetails(null)} 
          />
        </div>
      )}

      {/* Training Modal */}
      {showTraining && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          zIndex: 1000
        }}>
          <MobileTraining 
            reservation={showTraining} 
            onClose={() => setShowTraining(false)} 
          />
        </div>
      )}

      {/* Reservation Form Modal */}
      {showReservationForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          zIndex: 1000
        }}>
          <MobileReservationForm
            key={editingReservation?.id || 'new'}
            reservation={editingReservation}
            selectedDate={selectedDate}
            onClose={() => { setShowReservationForm(false); setEditingReservation(null); }}
            onSuccess={() => {
              const dateStr = formatDate(selectedDate);
              fetchReservations(dateStr, dateStr);
            }}
          />
        </div>
      )}

      {/* Quick Date Navigation */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        left: '20px',
        right: '20px',
        display: 'flex',
        gap: '10px',
        justifyContent: 'center'
      }}>
        <button
          onClick={() => setSelectedDate(new Date())}
          style={{
            padding: '12px 20px',
            backgroundColor: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '25px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          Today
        </button>
        <button
          onClick={() => {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            setSelectedDate(tomorrow);
          }}
          style={{
            padding: '12px 20px',
            backgroundColor: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '25px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          Tomorrow
        </button>
      </div>
    </div>
  );
};

export default MobileCalendar;